import React, { useLayoutEffect, useRef, useState } from "react"
import classNames from "classnames"
import { useIntl } from "gatsby-plugin-intl"
import { useMixpanel } from "gatsby-plugin-mixpanel"
import { useFormik } from "formik"
import * as yup from "yup"
import { debounce, uniqueId } from "lodash"

import * as styles from "./ContactFormEmail.module.scss"
import { useSiteMetadata } from "../../../hooks/useSiteMetadata"
import Button from "../Button/Button"
import { Link } from "../../Link"

import iconSuccess from "../../../assets/images/icons/form-success.svg"
import iconInvalid from "../../../assets/images/icons/form-invalid.svg"

const phoneRegExp = /^[0-9+ (]+[0-9 \-()]{1,15}$/
const notCorporateEmailDomains = [
	"gmail.com",
	"yandex.ru",
	"hotmail.com",
	"yahoo.com",
	"aol.com",
	"mail.ru",
	"mail.com",
]

const validationObject = {
	name: yup.string(),
	phone: yup
		.string()
		.matches(phoneRegExp, "contact-form.fields.phone.errors.incorrect"),
	email: yup
		.string()
		.email("contact-form.fields.email.errors.incorrect")
		.test(
			"is-corporate-email",
			"Only business email",
			(value) => {
				if (!value) {
					return false
				}
				const [, domain] = value.split("@")
				return !notCorporateEmailDomains.includes(domain)
			}
		)
		.required("contact-form.fields.email.errors.required"),
	message: yup.string(),
}

interface ContactFormProps {
	className?: string
	[key: string]: any
	title: string
	text: string
}

const ContactForm: React.FC<ContactFormProps> = ({ className, title, text, ...others }) => {
	const intl = useIntl()
	const id = uniqueId()
	const { siteUrl } = useSiteMetadata()
	const mixpanel = useMixpanel()

	// статус: null | success | error
	const [status, setStatus] = useState<null | "success" | "error">(null)

	const formik = useFormik({
		initialValues: {
			email: "",
			message: "",
		},
		validationSchema: yup.object().shape(validationObject),
		validateOnBlur: false,
		validateOnChange: false,
		onSubmit: (values) => {
			const { message, ...valuesWithoutMessage } = values
			mixpanel.track('Start the "Contact form" submission', {
				...valuesWithoutMessage,
			})
			fetch("https://formcarry.com/s/Ja2eF7VzLlh", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({
					...values,
					siteUrl,
					source: "Contact form",
				}),
			})
				.then((response) => {
					if (response.status === 200) {
						setStatus("success")
						formik.setSubmitting(false)
						mixpanel.track('Successfully submit "Contact form"', {
							...valuesWithoutMessage,
						})
					} else {
						setStatus("error")
					}
				})
				.catch((error) => {
					console.log(error)
					setStatus("error")
					mixpanel.track('Submission with error of "Contact form"', {
						...valuesWithoutMessage,
					})
				})
		},
	})

	const [isFormTouched, isFormTouchedSetter] = useState(false)
	const formElement = useRef(null)

	const onFieldChange = (event) => {
		if (!isFormTouched) {
			isFormTouchedSetter(true)
			mixpanel.track('Start to fill the "Contact form"')
		}
		formik.handleChange(event)
	}

	useLayoutEffect(() => {
		if (window) {
			const visibilityChecker = () => {
				const elementPosition =
					formElement &&
					formElement.current &&
					formElement.current.getBoundingClientRect()
				const viewportHeight =
					window.innerHeight || document.documentElement.clientHeight
				if (elementPosition && elementPosition.top < viewportHeight) {
					window.removeEventListener("scroll", debouncedVisibilityChecker)
					mixpanel.track('Scroll to "Contact form"')
				}
			}
			const debouncedVisibilityChecker = debounce(visibilityChecker, 100)
			if (formElement) {
				window.addEventListener("scroll", debouncedVisibilityChecker)
			}
		}
	}, [])

	const invalidClasses = classNames([styles.field], [styles.fieldInputError])

	const form = (
		<form
			className={styles.contactForm}
			onSubmit={formik.handleSubmit}
			noValidate
			ref={formElement}
			{...others}
		>
			<div>
				<h3 className={styles.ctaTitle}>{title}</h3>
				<p>{text}</p>
				<p className={styles.ctaRedTitle}>
					Let’s Talk
				</p>
			</div>

			<div className={styles.formGroup}>
				<input
					id={`email-${id}`}
					name="email"
					type="email"
					placeholder={intl.formatMessage({
						id: "contact-form.fields.email.placeholder",
					})}
					onChange={onFieldChange}
					value={formik.values.email}
					autoComplete="off"
					className={
						formik.touched["email"] && formik.errors["email"]
							? invalidClasses
							: styles.field
					}
				/>
				<label className={styles.fieldLabel} htmlFor={`email-${id}`}>
					{intl.formatMessage({ id: "contact-form.fields.email.label" })}
				</label>
				<span className={styles.fieldErrorMessage}>
					{formik.touched["email"] && formik.errors["email"]
						? intl.formatMessage({ id: formik.errors["email"] })
						: null}
				</span>
			</div>

			<div className={styles.submitBlock}>
				<Button type="submit" variant="fill">
					{intl.formatMessage({ id: "contact-form.submit" })}
				</Button>
				<p className={styles.fieldAccept}>
					{intl.formatMessage({
						id: "contact-form.fields.file.text-before-link",
					})}
					<Link to="/personal-data-processing">
						{intl.formatMessage({ id: "contact-form.fields.file.text-link" })}
					</Link>
					{intl.formatMessage({
						id: "contact-form.fields.file.text-after-link",
					})}
				</p>
			</div>
		</form>
	)

	const formSuccessfulSubmission = (
		<div className={styles.successBlock}>
			<img src={iconSuccess} alt="" />
			<h3>
				{intl.formatMessage({
					id: "contact-form.success",
					defaultMessage: "Success",
				})}
			</h3>
		</div>
	)

	const formErrorSubmission = (
		<div className={styles.errorBlock}>
			<img src={iconInvalid} alt="Invalid" />
			<h3>
				{intl.formatMessage({
					id: "contact-form.error"
				})}
			</h3>

		</div>
	)

	const renderedContent =
		status === "success"
			? formSuccessfulSubmission
			: status === "error"
				? formErrorSubmission
				: form

	return <>{renderedContent}</>
}

export default ContactForm
